// Inset-shadow renderer for Canvas-baked image effects (P-037.29 D-137
// §item 4 — revised at post-Phase-Close §item G to use a cleaner Gaussian-
// blur stroke approach instead of four directional gradients).
//
// **Why this exists.** Both `image-resize.ts` (upload pipeline) and
// `image-composite.ts` (Big-3 + hero composite pipelines) bake an inset
// shadow into the image bytes so the visual effect renders in every
// email client (Outlook desktop silently drops `box-shadow: inset` CSS).
// Centralizing the renderer keeps the algorithm + opacity locked in one
// place — drift between the two consumers would be visible side-by-side
// in the same rendered email.
//
// **Algorithm — Gaussian-blurred stroke clipped to the inset rect
// (revised at post-Phase-Close §item G).** Mathematically equivalent to
// CSS `box-shadow: inset 0 0 ${blurPx}px rgba(0, 0, 0, ${opacity})`:
//
// 1. Clip the canvas to the visible inset rect so anything painted
//    outside is discarded.
// 2. Draw a stroked rect at the rect's edge. `strokeRect` centers the
//    stroke on the edge — half inside (visible), half outside (clipped).
// 3. `ctx.filter = 'blur(N/2)'` disperses the stroke's pixels radially.
//    The Gaussian distribution is uniform — corners receive the same
//    density as mid-edges (no corner doubling).
//
// **Why this beats the prior four-gradient approach.** The old
// `applyInsetShadow` painted four linear gradients (one per edge), each
// fading dark→clear from the edge inward. At the corners, two gradients
// overlapped — the alpha-blend at corner mid-thickness summed to roughly
// 2× the mid-edge opacity (≈ 0.58 effective at corners when global was
// 0.35), producing visible dark "wings" in the four corners. Banks's
// P-037.29 post-Phase-Close screenshot review surfaced this. The
// Gaussian-blur stroke produces a uniform soft ring with no corner
// build-up.
//
// **Browser support.** `ctx.filter` on `OffscreenCanvasRenderingContext2D`
// landed in Chromium 76, Safari 17, Firefox 75 — same vintage as
// `OffscreenCanvas.prototype.convertToBlob`, which the pipeline already
// requires (`isCompositeSupported` / `isResizeSupported` gates). No new
// support gate needed.

export interface InsetShadowOpts {
  /** Blur radius in PIXEL space (whatever the consumer's canvas units
   *  are — pass the @2x value when drawing on a @2x retina canvas). */
  readonly blurPx: number
  /** 0–1 opacity. Default 0.25 per Banks's post-Phase-Close §item G
   *  pick (was 0.35 pre-revision; the cleaner algorithm distributes
   *  pixels more evenly than the four-gradient predecessor so the
   *  lower opacity reads roughly the same at mid-edges). */
  readonly opacity?: number
}

/** Default opacity per P-037.29 post-Phase-Close §item G (Banks-confirmed
 *  at planning). */
export const DEFAULT_INSET_SHADOW_OPACITY = 0.25

/** Default visible blur in CSS px per the original D-131 design intent
 *  (matches the prior four-gradient implementation). Consumers on @2x
 *  canvases pass `DEFAULT_INSET_SHADOW_BLUR_PX * 2`. */
export const DEFAULT_INSET_SHADOW_BLUR_PX = 16

/**
 * Bake an inset shadow into a sub-rect of a canvas. Used by both the
 * upload-time resize pipeline (when `ImageTarget.bakeInsetShadow` is
 * set) and the composite pipelines (Big-3 + hero — on the event-image
 * region only, before the navy bar + logo overlays).
 *
 * Mathematically equivalent to CSS `box-shadow: inset 0 0 ${blurPx}px
 * rgba(0, 0, 0, ${opacity})`. Renders the same in every client.
 *
 * The (x, y, width, height) rect is in the canvas's pixel coordinate
 * system — for an @2x canvas, pass @2x values.
 */
export function applyInsetShadow(
  ctx: OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  opts: InsetShadowOpts,
): void {
  const opacity = opts.opacity ?? DEFAULT_INSET_SHADOW_OPACITY
  ctx.save()
  // Clip to the inset rect so the outer half of the stroke + its blurred
  // pixels outside the rect get cut away. The inner half + its inward
  // blur lands inside the visible area — that's the inset shadow.
  ctx.beginPath()
  ctx.rect(x, y, width, height)
  ctx.clip()
  // Stroke centered on the rect edge with Gaussian blur. `lineWidth =
  // blurPx` means the stroke extends `blurPx / 2` inside the rect; the
  // `filter: blur(blurPx / 2)` disperses pixels another `blurPx / 2` in
  // each direction. Total inward extent: ~`blurPx`, matching CSS
  // `box-shadow: inset 0 0 ${blurPx}px ...`.
  ctx.filter = `blur(${opts.blurPx / 2}px)`
  ctx.strokeStyle = `rgba(0, 0, 0, ${opacity})`
  ctx.lineWidth = opts.blurPx
  ctx.strokeRect(x, y, width, height)
  ctx.filter = 'none'
  ctx.restore()
}
